import { request } from '@/utils/request'
import { jwt } from '@/utils/jwt.js'
import Vue from 'vue'

function arrayRemove(array, element) {
    if (!array) {
        return
    }

    var index = array.indexOf(element)

    if (index > -1) {
        array.splice(index, 1)
    }
}

function mapToArrayAndPush(collection, id, element) {
    if (!collection[id]) {
        collection[id] = []
    }

    collection[id].push(element)
}

export default class {
    constructor() {
        this._loading = 0
        this.populated = false
        this.entities = {
            company: {},
            executive: {},
        }
        this.__watchlists = []
        this.watchlistsById = {}
        this.version = 0
        this._populate()
    }

    get watchlists() {
        return this.__watchlists
    }

    get loading() {
        return !this.populated || this._loading > 0
    }

    refresh() {
        return this._populate(true)
    }

    async add(name) {
        const response = await this._post(`/api/watchlist`, { name })
        this._apply(response.content)
    }

    async remove(id) {
        await this._delete(`/api/watchlist/${id}`)
        return this._remove(id)
    }

    addEntity(watchlistId, entityType, entityId, userInitiated = true) {
        return this.addEntities(watchlistId, [{ entityType, entityId }], userInitiated)
    }

    async addEntities(watchlistId, entities, userInitiated = true) {
        const watchlist =
            watchlistId === 0 ? this.__watchlists.find(w => w.name == 'Default') : this.__watchlists[watchlistId]

        if (!userInitiated && (!watchlist || watchlist?.userModified || watchlist?.companies.length >= 5)) {
            return
        }

        const response = await this._post(`/api/tracklists/${watchlistId}/entities`, { userInitiated, entities })

        if (response.ok || response.status === 409) {
            this._apply(response.content)
        }
    }

    removeEntity(watchlistId, entityType, entityId) {
        return this.removeEntities(watchlistId, [{ entityType, entityId }])
    }

    async removeEntities(watchlistId, entities) {
        const response = await this._delete(`/api/tracklists/${watchlistId}/entities`, entities)
        return this._apply(response.content)
    }

    async getWatchlistsForEntity(entityType, entityId) {
        await this._populate()
        return (this.entities[entityType.toLowerCase()] || {})[entityId] || []
    }

    _get(url) {
        return this._request('get', url)
    }

    _post(url, data) {
        return this._request('post', url, data)
    }

    async _put(url, data) {
        return await this._request('put', url, data)
    }

    _delete(url, data) {
        return this._request('delete', url, data)
    }

    async _request(method, url, body) {
        const config = {
            method,
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
        }

        // attempt to detect overlapping calls to avoid drift where possible
        // this should help reduce cases where the client and server are out of sync
        // definitely does not catch any multi-device/multi-browser/mulit-tab scenarios

        // advance the version number
        const version = ++this.version

        const response = await request(url, config)

        // if version number has changed between request and response, there is a
        // possibility that the calls overlapped and the results are unknown
        if (version != this.version) {
            setTimeout(() => this._populate(true), 1000)
        }

        return response
    }

    async _populate(force) {
        if (!jwt.authenticated || ((this.populated || this._loading > 0) && !force) || this._loading > 5) {
            return
        }

        try {
            this._loading++

            var response = await this._get(`/api/tracklists`)

            if (response.content?.forEach) {
                response.content.forEach(watchlist => this._apply(watchlist))
            }
        } finally {
            this._loading--
            this.populated = true
        }
    }

    _apply(watchlist) {
        this._remove(watchlist.id)
        this._insert(watchlist)
    }

    _remove(id) {
        var watchlist = this.watchlistsById[id]

        if (!watchlist) {
            return
        }

        arrayRemove(this.__watchlists, watchlist)
        watchlist.companies.forEach(c => arrayRemove(this.entities.company[c.id], watchlist))
        watchlist.executives.forEach(c => arrayRemove(this.entities.executive[c.id], watchlist))
    }

    _insert(watchlist) {
        this.__watchlists.push(watchlist)

        Vue.set(this.watchlistsById, watchlist.id, watchlist)

        watchlist.companies.forEach(c => mapToArrayAndPush(this.entities.company, c.id, watchlist))
        watchlist.executives.forEach(e => mapToArrayAndPush(this.entities.executive, e.id, watchlist))
    }
}
